'use client';

import React, { useState, useEffect } from 'react';
import { Grid, Text, Box, Notification, Stack } from '@mantine/core';
import SelectionCard from '../SelectionCard';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useTranslations } from 'next-intl';

interface HealthGoalsStepProps {
  healthGoals: string[];
  onHealthGoalSelect: (value: string) => void;
}

const HealthGoalsStep: React.FC<HealthGoalsStepProps> = ({ healthGoals, onHealthGoalSelect }) => {
  const t = useTranslations('healthGoals');
  const [notificationVisible, setNotificationVisible] = useState(false);
  const isMobile = useMediaQuery('(max-width: 767px)');
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1023px)');

  const [visibleCards, setVisibleCards] = useState<number[]>([]);

  const healthGoalsOptions = [
    {
      value: 'improvedInsulinSensitivity',
      label: t('improvedInsulinSensitivity.label'),
      description: t('improvedInsulinSensitivity.description'),
      image: '/images/improvedInsulinSensitivity.jpeg',
    },
    {
      value: 'weightLoss',
      label: t('weightLoss.label'),
      description: t('weightLoss.description'),
      image: '/images/weightLoss.jpeg',
    },
    {
      value: 'decreasedVisceralFat',
      label: t('decreasedVisceralFat.label'),
      description: t('decreasedVisceralFat.description'),
      image: '/images/decreasedVisceralFat.jpeg',
    },
    {
      value: 'lowerCholesterol',
      label: t('lowerCholesterol.label'),
      description: t('lowerCholesterol.description'),
      image: '/images/lowerCholesterol.jpeg',
    },
    {
      value: 'reducedInflammation',
      label: t('reducedInflammation.label'),
      description: t('reducedInflammation.description'),
      image: '/images/reducedInflammation.jpeg',
    },
    {
      value: 'increasedEnergy',
      label: t('increasedEnergy.label'),
      description: t('increasedEnergy.description'),
      image: '/images/increasedEnergy.jpeg',
    },
    {
      value: 'improvedMood',
      label: t('improvedMood.label'),
      description: t('improvedMood.description'),
      image: '/images/improvedMood.jpeg',
    },
    {
      value: 'decreasedRisk',
      label: t('decreasedRisk.label'),
      description: t('decreasedRisk.description'),
      image: '/images/decreasedRisk.jpeg',
    },
    {
      value: 'longevity',
      label: t('longevity.label'),
      description: t('longevity.description'),
      image: '/images/longevity.jpeg',
    },
  ];

  useEffect(() => {
    const showNextCard = (index: number) => {
      if (index < healthGoalsOptions.length) {
        setVisibleCards((prev) => [...prev, index]);
        setTimeout(() => showNextCard(index + 1), 200); // Adjust delay as needed
      }
    };

    showNextCard(0);

    return () => {
      // Clear any remaining timeouts
      let i = 0;
      while (i < healthGoalsOptions.length) {
        clearTimeout(i);
        i++;
      }
    };
  }, []);

  const handleSelect = (value: string) => {
    if (healthGoals.includes(value)) {
      onHealthGoalSelect(value);
    } else if (healthGoals.length < 3) {
      onHealthGoalSelect(value);
    } else {
      setNotificationVisible(true);
      setTimeout(() => setNotificationVisible(false), 3000);
    }
  };

  return (
    <Box p={isMobile ? 'xs' : 'md'}>
      <Stack gap={isMobile ? 'xs' : 'md'}>
        <Text size={isMobile ? 'md' : 'lg'} fw={500}>
          {t('selectYourHealthGoals')}
        </Text>
        {notificationVisible && (
          <Notification
            title={t('notification.title')}
            color="red"
            onClose={() => setNotificationVisible(false)}
          >
            {t('notification.message')}
          </Notification>
        )}
        <Grid gutter={isMobile ? 'xs' : 'md'}>
          {healthGoalsOptions.map((option, index) => (
            <Grid.Col
              key={option.value}
              span={isMobile ? 12 : isTablet ? 6 : 4}
              style={{
                opacity: visibleCards.includes(index) ? 1 : 0,
                transform: `translateY(${visibleCards.includes(index) ? '0' : '20px'})`,
                transition: 'opacity 0.5s ease-in-out, transform 0.5s ease-in-out',
                visibility: visibleCards.includes(index) ? 'visible' : 'hidden',
              }}
            >
              <SelectionCard
                value={option.value}
                label={option.label}
                description={option.description}
                image={option.image}
                onSelect={handleSelect}
                isSelected={healthGoals.includes(option.value)}
              />
            </Grid.Col>
          ))}
        </Grid>
      </Stack>
    </Box>
  );
};

export default HealthGoalsStep;
